import { Chip, Stack, Tooltip } from '@mui/material';
import {
    Button as RaButton,
    DatagridConfigurable,
    ExportButton,
    List,
    SearchInput,
    TextField,
    TopToolbar,
    useRecordContext,
} from 'react-admin';
import { useState } from 'react';

import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import { InviteUserDialog } from '../admin/InviteUserDialog';
import { UserLegalField } from './UserLegalField';

const UsersListActions = ({ onInvite }: { onInvite: () => void }) => (
    <TopToolbar>
        <RaButton label="Invite user" onClick={onInvite} variant="contained" />
        <ExportButton />
    </TopToolbar>
);

const filters = [<SearchInput source="q" alwaysOn />];

interface FieldProps {
    label?: string | boolean;
}

const AccountTypeField = (_props: FieldProps) => {
    const record = useRecordContext();
    if (!record) return null;

    return (
        <Stack direction="row" gap={1} alignItems="center">
            {record.account_type === "company" ? (
                <>
                    <BusinessIcon color="primary" />
                    {record.company && <span>{record.company}</span>}
                </>
            ) : (
                <PersonIcon />
            )}
        </Stack>
    );
};

const GenderField = (_props: FieldProps) => {
    const record = useRecordContext();
    if (!record) return null;

    return record.gender === "male" ?
        <MaleIcon color="primary" /> :
        <FemaleIcon color="secondary" />;
};

const StatusField = (_props: FieldProps) => {
    const record = useRecordContext();
    if (!record || !record.status) return null;

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'invited': return 'warning';
            case 'approved': return 'success';
            case 'disabled': return 'error';
            default: return 'default';
        }
    };

    const chip = (
        <Chip
            label={record.status.charAt(0).toUpperCase() + record.status.slice(1)}
            size="small"
            variant="outlined"
            color={getStatusColor(record.status)}
        />
    );

    if (record.status === 'invited' && record.invite_sent_at?.toDate) {
        const sentAt = record.invite_sent_at.toDate() as Date;
        const expiresAt = record.invite_expires_at?.toDate?.() as Date | undefined;
        const tip = expiresAt
            ? `Invited ${sentAt.toLocaleString()} — expires ${expiresAt.toLocaleString()}`
            : `Invited ${sentAt.toLocaleString()}`;
        return (
            <Tooltip
                title={tip}
                slotProps={{ tooltip: { sx: { maxWidth: 'none', whiteSpace: 'nowrap' } } }}
            >
                {chip}
            </Tooltip>
        );
    }

    return chip;
};

const RoleField = (_props: FieldProps) => {
    const record = useRecordContext();
    if (!record || !record.role) return null;

    const getRoleColor = (role: string) => {
        switch (role) {
            case 'admin': return 'primary';
            default: return 'default';
        }
    };

    return (
        <Chip
            label={record.role.charAt(0).toUpperCase() + record.role.slice(1)}
            size="small"
            variant="outlined"
            color={getRoleColor(record.role)}
        />
    );
};


export function UsersList() {
    const [inviteOpen, setInviteOpen] = useState(false);
    return (
        <Stack gap={4}>
            <List
                resource="users"
                filters={filters}
                actions={<UsersListActions onInvite={() => setInviteOpen(true)} />}
                sort={{ field: 'created_at', order: 'DESC' }}
            >
                <DatagridConfigurable
                    rowClick={(id) => `/admin/users/${id}`}
                    bulkActionButtons={false}
                >
                    <AccountTypeField label="Account" />
                    <TextField source="first_name" />
                    <TextField source="last_name" />
                    <TextField source="email" />
                    <TextField source="referred_by" />
                    <RoleField label="Role" />
                    <StatusField label="Status" />
                    <UserLegalField label="Legal" />
                </DatagridConfigurable>
            </List>
            <InviteUserDialog
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
            />
        </Stack>
    );
}
